import { useApi, useMessage } from 'app/context';
import { buildErrorDetails, buildErrorMessage, trimFileName } from 'app/utils';
import {
  IPDFConfigAttachmentType,
  IPDFConfigurationCreate,
  IPDFConfigurationSchema,
  IPDFConfigurationUpdate,
} from 'generated/axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from './useForm';
import { t } from 'i18next';

export const usePdfConfiguration = () => {
  const { pdfConfigApi } = useApi();
  const { error, success } = useMessage();

  const [loading, setLoading] = useState<boolean>(false);
  const [detail, setDetail] = useState<IPDFConfigurationSchema>();
  const [request, setRequest] = useState<IPDFConfigurationCreate | IPDFConfigurationUpdate>({
    //options false as default (prevents undefined in create when not checked)
    patient_data_options: {
      show_birth_date: false,
      show_company: false,
      show_job_title: false,
      show_residence: false,
      show_tax_code: false,
    },
    report_data_options: {
      show_comment: false,
      show_doctor_name: false,
      show_doctor_signature: false,
      show_execution_date: false,
      show_patient_signature: false,
      show_reporting_date: false,
      show_temperature: false,
      show_urine_quantity: false,
    },
  });

  const buildRequestFromDetail = (
    data: IPDFConfigurationSchema | undefined,
  ): IPDFConfigurationUpdate | IPDFConfigurationCreate => ({
    header_title: data?.header_title, //company name
    header_subtitle: data?.header_subtitle, //address
    patient_data_options: data?.patient_data_options,
    report_data_options: data?.report_data_options,
    free_text_1: data?.free_text_1, //post result text
    privacy_policy: data?.privacy_policy,
  });

  useEffect(() => {
    setRequest(buildRequestFromDetail(detail));
  }, [detail]);

  const get = useCallback(() => {
    setLoading(true);
    pdfConfigApi
      .webGetPDFConfiguration()
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        setDetail(resp.data);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfConfigApi]);

  useEffect(() => {
    get();
  }, [get]);

  const updateConfiguration = useCallback(
    (form: IPDFConfigurationCreate | IPDFConfigurationUpdate) => {
      setLoading(true);
      pdfConfigApi
        .webUpdatePDFConfiguration(form)
        .then((resp) => {
          if (resp.status >= 400) {
            return Promise.reject(resp);
          }
          success({ message: t('pdfConfig.updateSuccess') as string });
          setDetail(resp.data);
        })
        .catch((err) => {
          error({ message: buildErrorMessage(err) });
          updateForm.setErrors(buildErrorDetails(err));
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pdfConfigApi],
  );

  const uploadLogo = useCallback(
    (file: File) => {
      setLoading(true);
      pdfConfigApi
        .webUploadPDFConfigurationAttachment(IPDFConfigAttachmentType.Logo, file)
        .then((resp) => {
          if (resp.status >= 400) {
            return Promise.reject(resp);
          }
          success({ message: t('pdfConfig.uploadLogoSuccess') as string });
          setDetail(resp.data);
        })
        .catch((err) => {
          error({ message: buildErrorMessage(err) });
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pdfConfigApi],
  );

  const downloadLogo = useCallback(() => {
    setLoading(true);
    pdfConfigApi
      .webDownloadPDFConfigurationAttachment(IPDFConfigAttachmentType.Logo, { responseType: 'blob' })
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        const blob = new Blob([resp.data as unknown as BlobPart]);
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = trimFileName(resp.headers?.['content-disposition']) ?? 'download';
        a.click();

        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfConfigApi]);

  const updateForm = useForm<IPDFConfigurationCreate | IPDFConfigurationUpdate>({
    defaultValues: request,
    onSubmit: (form) => updateConfiguration(form),
  });

  return {
    detail,
    updateForm,
    loading,
    uploadLogo,
    downloadLogo,
  };
};
